/** @jsxImportSource @emotion/react */
import { forwardRef, useEffect, useRef } from 'react'
import { videoPlayerStyle } from '../styles'

interface VideoPlayerProps {
  url?: string
  offsetSeconds?: number
}

const extractYouTubeId = (url: string): string => {
  if (!url) {
    return ''
  }

  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)

  return match && match[2].length === 11 ? match[2] : ''
}

const isYouTubeUrl = (url: string): boolean => {
  if (!url) {
    return false
  }
  return url.includes('youtube.com') || url.includes('youtu.be')
}

const isDirectVideoUrl = (url: string): boolean => {
  if (!url) {
    return false
  }
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi']
  return videoExtensions.some((ext) => url.toLowerCase().includes(ext))
}

const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>(
  ({ url, offsetSeconds }, ref) => {
    const videoRef = useRef<HTMLVideoElement | null>(null)

    const setRefs = (node: HTMLVideoElement | null) => {
      videoRef.current = node
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    }

    // Use useEffect to autoplay direct video files when URL changes
    useEffect(() => {
      if (videoRef.current && url && isDirectVideoUrl(url)) {
        videoRef.current.load()
        // Attempt to autoplay (may be blocked by browser policies)
        const playPromise = videoRef.current.play()
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay was prevented, which is normal browser behavior
          })
        }
      }
    }, [url])

    // Handle offset for direct video files
    useEffect(() => {
      const video = videoRef.current
      if (video && offsetSeconds && isDirectVideoUrl(url || '')) {
        const handleLoadedData = () => {
          if (videoRef.current) {
            videoRef.current.currentTime = offsetSeconds
          }
        }

        video.addEventListener('loadeddata', handleLoadedData)

        // If video is already loaded, apply offset immediately
        if (video.readyState >= 2) {
          handleLoadedData()
        }

        return () => {
          video.removeEventListener('loadeddata', handleLoadedData)
        }
      }
    }, [url, offsetSeconds])

    // Handle YouTube videos
    if (isYouTubeUrl(url || '')) {
      const videoId = extractYouTubeId(url || '')

      if (!videoId) {
        return (
          <div css={videoPlayerStyle}>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#9FA3A9',
                fontSize: '16px',
              }}
            >
              Invalid YouTube URL
            </div>
          </div>
        )
      }

      const startTime = offsetSeconds
        ? `&start=${Math.floor(offsetSeconds)}`
        : ''

      return (
        <div css={videoPlayerStyle}>
          <iframe
            key={`${url}-${offsetSeconds || 0}`}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0${startTime}`}
            title='Video Player'
            allowFullScreen
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          />
        </div>
      )
    }

    // Handle direct video files (S3, etc.)
    if (isDirectVideoUrl(url || '')) {
      return (
        <div css={videoPlayerStyle}>
          <video
            ref={setRefs}
            controls
            autoPlay
            muted
            width='100%'
            height='100%'
            css={{
              objectFit: 'contain' as const,
              backgroundColor: '#000',
            }}
          >
            <source src={url} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        </div>
      )
    }

    // No valid video URL provided
    return (
      <div css={videoPlayerStyle}>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#9FA3A9',
            fontSize: '16px',
          }}
        >
          No video available
        </div>
      </div>
    )
  }
)

VideoPlayer.displayName = 'VideoPlayer'

export default VideoPlayer
